import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Star, MessageSquare, User, BookOpen } from 'lucide-react';

export default function TeacherReviews() {
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    useEffect(() => {
        api.get('/courses').then(r => {
            const list = r.data?.data || r.data || [];
            setCourses(list);
            if (list.length > 0) loadReviews(list[0].id);
            else setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const loadReviews = async (courseId) => {
        setSelectedCourse(courseId);
        setReviewsLoading(true);
        try {
            const r = await api.get(`/courses/${courseId}/reviews`);
            setReviews(r.data?.data || r.data || []);
        } catch { setReviews([]); }
        finally { setReviewsLoading(false); setLoading(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>;

    const currentCourse = courses.find(c => c.id === selectedCourse);

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-8 text-white">
                <h1 className="text-2xl font-bold mb-2 flex items-center gap-2"><Star className="w-6 h-6" /> Sharhlar</h1>
                <p className="text-amber-100">Talabalar tomonidan qoldirilgan barcha sharhlar</p>
            </div>

            {courses.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Hali kurslaringiz yo'q</h3>
                    <Link to="/teacher/courses" className="text-blue-600 text-sm font-medium">Kurs yaratish →</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Course selector */}
                    <div className="space-y-2">
                        {courses.map(c => (
                            <button key={c.id} onClick={() => loadReviews(c.id)}
                                className={`w-full text-left p-4 rounded-xl border transition text-sm
                                    ${selectedCourse === c.id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                        : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                <div className="font-semibold mb-1">{c.title}</div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    {c.rating > 0 && <span className="flex items-center gap-0.5 text-yellow-500"><Star className="w-3 h-3" /> {c.rating}</span>}
                                    <span>{c.enrollments_count || c.students_count || 0} talaba</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Reviews list */}
                    <div className="lg:col-span-3">
                        {currentCourse && (
                            <div className="flex items-center gap-3 mb-4">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{currentCourse.title}</h2>
                                {currentCourse.rating > 0 && (
                                    <span className="flex items-center gap-1 text-yellow-500 text-sm font-medium">
                                        <Star className="w-4 h-4" /> {currentCourse.rating}
                                    </span>
                                )}
                            </div>
                        )}

                        {reviewsLoading ? (
                            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div>
                        ) : reviews.length > 0 ? (
                            <div className="space-y-3">
                                {reviews.map(r => (
                                    <div key={r.id} className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                                {r.user?.name?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{r.user?.name}</span>
                                                    <div className="flex gap-0.5">
                                                        {[1,2,3,4,5].map(s => (
                                                            <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 dark:text-gray-600'}`} />
                                                        ))}
                                                    </div>
                                                    <span className="text-xs text-gray-400 ml-auto">{new Date(r.created_at).toLocaleDateString('uz')}</span>
                                                </div>
                                                {r.comment && <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{r.comment}</p>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
                                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 dark:text-gray-400">Bu kursga hali sharh yozilmagan</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
